import { Button } from '@approvalmax/ui';
import { Box, Flex, Form, Popup } from '@approvalmax/ui/src/components';
import { errorHelpers, hooks } from '@approvalmax/utils';
import { selectors } from 'modules/common';
import { domain, State } from 'modules/data';
import { useProgressLoading } from 'modules/utils/hooks/useProgressLoading';
import { memo, useCallback, useEffect, useMemo, useState } from 'react';
import Skeleton from 'react-loading-skeleton';
import { useDispatch, useSelector } from 'react-redux';
import { useToggle, useUnmount } from 'react-use';
import { AddBillInvoicesPopup, AmaxPayBankAccountInfoCard, BankLogoImage } from 'shared/components';
import { useFilters } from 'shared/components/AddBillInvoicesPopup/AddBillInvoicesPopup.hooks';
import {
    CheckAmaxPayPaymentDetailsDiscrepancies,
    useCheckAmaxPayPaymentDetailsDiscrepancies,
    useGetAmaxPayXeroBankAccounts,
} from 'shared/data/webApp/v1';

import { updateXeroAmaxPayBPDetails } from '../../../../../actions';
import { RequesterInstructionSection } from '../../../../../components';
import { getContext } from '../../../../../selectors/pageSelectors';
import ActionsBlock from '../../../../form/ActionsBlock/ActionsBlock';
import { useXeroAmaxPayBatchPaymentFormContext } from '../../XeroAmaxPayBatchPaymentForm.context';
import { FilterTrasferData } from '../../XeroAmaxPayBatchPaymentForm.types';
import { BillsInformation } from '../BillsInformation/BillsInformation';
import {
    DeletedPaymentDetailsPopupContent,
    PaymentDetailsDiscrepanciesPopupContent,
    SelectBankAccountPopupContent,
} from './components';
import { createMapFilterTransferWrapper } from './XeroAmaxPayBatchPaymentFormContent.helpers';
import { useBillsForBatchPaymentData } from './XeroAmaxPayBatchPaymentFormContent.hooks';
import { messages } from './XeroAmaxPayBatchPaymentFormContent.messages';
import {
    BatchPaymentBills,
    Request,
    Root,
    StyledLoadingBar,
    Subheader,
    SubheaderButton,
    SubheaderButtonsWrapper,
    Toolbar,
    ToolbarHeader,
    ToolbarHeaderText,
} from './XeroAmaxPayBatchPaymentFormContent.styles';
import { XeroAmaxPayBatchPaymentFormProps } from './XeroAmaxPayBatchPaymentFormContent.types';

export const XeroAmaxPayBatchPaymentFormContent = memo<XeroAmaxPayBatchPaymentFormProps>((props) => {
    const { request } = props;

    const dispatch = useDispatch();

    const [isOpenSelectBankAccountPopup, toggleIsOpenSelectBankAccountPopup] = useToggle(false);
    const company = useSelector((state: State) => selectors.company.getCompanyById(state, request.companyId));
    const [isOpenAddBillsPopup, toggleIsOpenAddBillsPopup] = useToggle(false);
    const [paymentDetailsDiscrepanciesOnChecking, setPaymentDetailsDiscrepanciesOnChecking] = useState<
        CheckAmaxPayPaymentDetailsDiscrepancies['response']['items'] | null
    >(null);
    const {
        clearItems,
        setShowErrors,
        saveAndSubmit,
        isSaving,
        billBatchPayment,
        setBillBatchPayment,
        selectedSimplifiedBankAccount,
        setSelectedSimplifiedBankAccount,
        deletedPaymentDetails,
        setDeletedPaymentDetails,
        paymentDetailsDiscrepancies,
        setPaymentDetailsDiscrepancies,
        applyPaymentDetailsDiscrepancies,
        referencesForMergedItems,
        addBills,
    } = useXeroAmaxPayBatchPaymentFormContext();

    const mapFilterTransferWithRequestCurrency = useMemo(
        () => createMapFilterTransferWrapper(request.currency),
        [request.currency]
    );

    const {
        initialTransferFilterData,
        sortingConfig,
        filterTransfer,
        setFilterTransfer,
        changeSortingConfig,
        applyFilters,
        resetFilter,
        form: filtersForm,
    } = useFilters<FilterTrasferData>(mapFilterTransferWithRequestCurrency);

    const { billsForBatchPaymentData, billsForBatchPaymentLoading } = useBillsForBatchPaymentData({
        requestId: request.id,
        companyId: request.companyId,
        companyCurrency: request.companyCurrency,
        filterTransfer,
        enabled: isOpenAddBillsPopup,
    });

    const pageContext = useSelector(getContext);

    const disableCheckPaymentDetails = useMemo(
        () => billBatchPayment.items.every((item) => !item.paymentDetails),
        [billBatchPayment.items]
    );

    const {
        mutateAsync: checkAmaxPayPaymentDetailsDiscrepancies,
        isLoading: isLoadingCheckAmaxPayPaymentDetailsDiscrepancies,
    } = useCheckAmaxPayPaymentDetailsDiscrepancies();

    const { data: amaxPayXeroBankAccounts, isLoading: isLoadingGetAmaxPayXeroBankAccounts } =
        useGetAmaxPayXeroBankAccounts({
            path: {
                companyId: request.companyId,
                requestId: request.id,
            },
        });

    const bankAccounts = amaxPayXeroBankAccounts?.items;

    const [showBankAccountsPopupQueryParam, , cleanShowBankAccountsPopupQueryParam] =
        hooks.useQueryParam('showBankAccountsPopup');

    useEffect(() => {
        if (showBankAccountsPopupQueryParam) {
            cleanShowBankAccountsPopupQueryParam();
            toggleIsOpenSelectBankAccountPopup(true);
        }
    }, [cleanShowBankAccountsPopupQueryParam, showBankAccountsPopupQueryParam, toggleIsOpenSelectBankAccountPopup]);

    useEffect(() => {
        if (selectedSimplifiedBankAccount && !selectedSimplifiedBankAccount.currencyCode) {
            const selectedBankAccount = bankAccounts?.find(
                (bankAccount) => bankAccount.id === selectedSimplifiedBankAccount.id
            );

            if (selectedBankAccount && selectedBankAccount.id === selectedSimplifiedBankAccount.id) {
                setSelectedSimplifiedBankAccount({
                    id: selectedBankAccount.id,
                    name: selectedBankAccount.nickname,
                    iconUrl: selectedBankAccount.bank.iconUrl,
                    number: selectedBankAccount.identification.bankAccountNumber,
                    sortCode: selectedBankAccount.identification.sortCode,
                    iban: selectedBankAccount.identification.iban,
                    currencyCode: selectedBankAccount.currencyCode,
                    connected: selectedBankAccount.isConnected,
                });
            }
        }
    }, [bankAccounts, selectedSimplifiedBankAccount, setSelectedSimplifiedBankAccount]);

    const handleSubmitFilters = filtersForm.handleSubmit(applyFilters);

    const handleAddBills = useCallback(
        (selectableBills: domain.AmaxPayBatchSelectableBillItem[]) => {
            addBills(selectableBills);
            filtersForm.reset();
            setFilterTransfer(initialTransferFilterData);
            toggleIsOpenAddBillsPopup(false);
        },
        [addBills, filtersForm, initialTransferFilterData, setFilterTransfer, toggleIsOpenAddBillsPopup]
    );

    const handleCheckPaymentDetailsDiscrepancies = useCallback(async () => {
        const body = billBatchPayment.items.reduce<CheckAmaxPayPaymentDetailsDiscrepancies['body']>((acc, item) => {
            if (item.paymentDetails && item.contact) {
                acc.push({
                    paymentDetailsVersionId: item.paymentDetails.versionId,
                    counterpartyId: item.contact.id,
                    currency: request.currency,
                });
            }

            return acc;
        }, []);
        const response = await checkAmaxPayPaymentDetailsDiscrepancies({
            body,
            params: {
                path: {
                    companyId: request.companyId,
                    requestId: request.id,
                },
            },
        });

        setPaymentDetailsDiscrepanciesOnChecking(response.items);
    }, [
        billBatchPayment.items,
        checkAmaxPayPaymentDetailsDiscrepancies,
        request.companyId,
        request.id,
        request.currency,
    ]);

    useProgressLoading(isSaving);

    useUnmount(() => {
        dispatch(updateXeroAmaxPayBPDetails(billBatchPayment));
    });

    const onSaveAndSubmit = useCallback(
        (commentText?: string) => {
            const newBillBatchPayment = billBatchPayment.isMergedBySupplier
                ? {
                      ...billBatchPayment,
                      items: billBatchPayment.items.map((item) => ({
                          ...item,
                          paymentReference: referencesForMergedItems[`${item.contact?.id},${item.paymentDetails?.id}`],
                      })),
                  }
                : billBatchPayment;

            setShowErrors(true);

            saveAndSubmit(commentText, newBillBatchPayment);
        },
        [billBatchPayment, referencesForMergedItems, saveAndSubmit, setShowErrors]
    );

    const onSelectBankAccount = useCallback(
        (bankAccountId: string) => {
            const selectedBankAccount = bankAccounts?.find((bankAccount) => bankAccount.id === bankAccountId);

            if (!selectedBankAccount) {
                throw errorHelpers.notFoundError();
            }

            setSelectedSimplifiedBankAccount({
                id: selectedBankAccount.id,
                name: selectedBankAccount.nickname,
                iconUrl: selectedBankAccount.bank.iconUrl,
                number: selectedBankAccount.identification.bankAccountNumber,
                sortCode: selectedBankAccount.identification.sortCode,
                iban: selectedBankAccount.identification.iban,
                currencyCode: selectedBankAccount.currencyCode,
                connected: selectedBankAccount.isConnected,
            });

            toggleIsOpenSelectBankAccountPopup(false);

            const newItems = billBatchPayment.items.map((item) => ({
                ...item,
                amaxPayBankAccount: {
                    id: bankAccountId,
                    text: selectedBankAccount.nickname,
                },
            }));

            setBillBatchPayment({ ...billBatchPayment, items: newItems }, 'items');
        },
        [
            bankAccounts,
            billBatchPayment,
            setBillBatchPayment,
            setSelectedSimplifiedBankAccount,
            toggleIsOpenSelectBankAccountPopup,
        ]
    );

    const onToggleDeletedPaymentDetailsPopup = useCallback(
        (newOpen: boolean) => {
            const isClosing = !newOpen;

            if (isClosing) {
                setDeletedPaymentDetails([]);
            }
        },
        [setDeletedPaymentDetails]
    );

    const closePaymentDetailsDiscrepanciesPopup = useCallback(
        () => setPaymentDetailsDiscrepancies([]),

        [setPaymentDetailsDiscrepancies]
    );

    const onTogglePaymentDetailsDiscrepanciesPopup = useCallback(
        (newOpen: boolean) => {
            if (!newOpen) {
                closePaymentDetailsDiscrepanciesPopup();
            }
        },
        [closePaymentDetailsDiscrepanciesPopup]
    );

    const handleApplyPaymentDetailsDiscrepancies = useCallback(() => {
        applyPaymentDetailsDiscrepancies();
        closePaymentDetailsDiscrepanciesPopup();
    }, [applyPaymentDetailsDiscrepancies, closePaymentDetailsDiscrepanciesPopup]);

    return (
        <>
            <Root>
                <Toolbar>
                    <ToolbarHeader>
                        <Flex alignItems='center' grow={1} spacing='4'>
                            <ToolbarHeaderText>{messages.headerText}</ToolbarHeaderText>
                        </Flex>

                        <ActionsBlock
                            customSaveAndSubmitRequest={onSaveAndSubmit}
                            isSaving={isSaving || isLoadingCheckAmaxPayPaymentDetailsDiscrepancies}
                        />
                    </ToolbarHeader>

                    <StyledLoadingBar $hide={!isLoadingCheckAmaxPayPaymentDetailsDiscrepancies} />
                </Toolbar>

                <Request>
                    <RequesterInstructionSection instructionText={pageContext.requesterInstruction} />

                    <Subheader>
                        <BatchPaymentBills>
                            {messages.batchPaymentBillsQty({ qty: billBatchPayment.items.length })}
                        </BatchPaymentBills>

                        <SubheaderButtonsWrapper>
                            <SubheaderButton
                                colorTheme='black'
                                execute={handleCheckPaymentDetailsDiscrepancies}
                                disabled={disableCheckPaymentDetails}
                            >
                                {messages.checkPaymentDetails}
                            </SubheaderButton>

                            <SubheaderButton
                                colorTheme='red'
                                execute={clearItems}
                                disabled={
                                    isLoadingCheckAmaxPayPaymentDetailsDiscrepancies ||
                                    billBatchPayment.items.length === 0
                                }
                            >
                                {messages.removeAllBills}
                            </SubheaderButton>

                            <Button
                                execute={toggleIsOpenAddBillsPopup}
                                disabled={
                                    isLoadingCheckAmaxPayPaymentDetailsDiscrepancies ||
                                    (!selectedSimplifiedBankAccount && !billBatchPayment.items.length)
                                }
                            >
                                {messages.addBills}
                            </Button>
                        </SubheaderButtonsWrapper>
                    </Subheader>

                    {(selectedSimplifiedBankAccount?.id ||
                        (!!billBatchPayment.items.length && selectedSimplifiedBankAccount?.id)) && (
                        <Box spacing='0 60 16'>
                            <AmaxPayBankAccountInfoCard
                                name={selectedSimplifiedBankAccount?.name}
                                bankLogo={
                                    selectedSimplifiedBankAccount?.iconUrl ? (
                                        <BankLogoImage
                                            src={selectedSimplifiedBankAccount?.iconUrl}
                                            width={36}
                                            height={36}
                                        />
                                    ) : (
                                        <Skeleton width={36} height={36} />
                                    )
                                }
                                currencyCode={
                                    isLoadingGetAmaxPayXeroBankAccounts ? (
                                        <Skeleton inline width={20} />
                                    ) : (
                                        selectedSimplifiedBankAccount?.currencyCode
                                    )
                                }
                                bankAccountNumber={
                                    isLoadingGetAmaxPayXeroBankAccounts ? (
                                        <Skeleton inline width={60} />
                                    ) : (
                                        selectedSimplifiedBankAccount?.number
                                    )
                                }
                                sortCode={
                                    isLoadingGetAmaxPayXeroBankAccounts ? (
                                        <Skeleton inline width={50} />
                                    ) : (
                                        selectedSimplifiedBankAccount?.sortCode
                                    )
                                }
                                iban={
                                    isLoadingGetAmaxPayXeroBankAccounts ? (
                                        <Skeleton inline width={60} />
                                    ) : (
                                        selectedSimplifiedBankAccount?.iban
                                    )
                                }
                                disconnected={!selectedSimplifiedBankAccount?.connected}
                                onEdit={toggleIsOpenSelectBankAccountPopup}
                            />
                        </Box>
                    )}

                    <BillsInformation
                        request={request}
                        toggleIsOpenSelectBankAccountPopup={toggleIsOpenSelectBankAccountPopup}
                        hasSelectedBankAccount={!!selectedSimplifiedBankAccount}
                        paymentDetailsDiscrepanciesOnChecking={paymentDetailsDiscrepanciesOnChecking}
                    />
                </Request>
            </Root>

            <Popup open={isOpenAddBillsPopup} onToggle={toggleIsOpenAddBillsPopup} size='large' qa='add-bill-invoices'>
                <Form form={filtersForm} onSubmit={handleSubmitFilters}>
                    <AddBillInvoicesPopup
                        billBatchPayment={billBatchPayment}
                        companyId={request.companyId}
                        requestId={request.id}
                        data={billsForBatchPaymentData}
                        isLoading={billsForBatchPaymentLoading}
                        resetFilter={resetFilter}
                        sortingConfig={sortingConfig}
                        changeSortingConfig={changeSortingConfig}
                        handleAddBills={handleAddBills}
                        attentionText={messages.addBillsAttentionText}
                    />
                </Form>
            </Popup>

            <Popup
                className='fs-exclude'
                open={isOpenSelectBankAccountPopup}
                onToggle={toggleIsOpenSelectBankAccountPopup}
                size='small'
            >
                <SelectBankAccountPopupContent
                    onSelectBankAccount={onSelectBankAccount}
                    bankAccounts={bankAccounts || []}
                    companyCurrency={company.defaultCurrency}
                    selectedBankAccountId={selectedSimplifiedBankAccount?.id}
                    progress={isLoadingGetAmaxPayXeroBankAccounts}
                />
            </Popup>

            <Popup
                open={!!paymentDetailsDiscrepancies.length}
                onToggle={onTogglePaymentDetailsDiscrepanciesPopup}
                size='medium'
            >
                <PaymentDetailsDiscrepanciesPopupContent
                    requestName={request.displayName}
                    paymentDetailsDiscrepancies={paymentDetailsDiscrepancies}
                    closePaymentDetailsDiscrepanciesPopup={closePaymentDetailsDiscrepanciesPopup}
                    handleApplyPaymentDetailsDiscrepancies={handleApplyPaymentDetailsDiscrepancies}
                />
            </Popup>

            <Popup open={!!deletedPaymentDetails.length} onToggle={onToggleDeletedPaymentDetailsPopup} size='medium'>
                <DeletedPaymentDetailsPopupContent
                    requestName={request.displayName}
                    deletedPaymentDetails={deletedPaymentDetails}
                    onToggleDeletedPaymentDetailsPopup={onToggleDeletedPaymentDetailsPopup}
                />
            </Popup>
        </>
    );
});

XeroAmaxPayBatchPaymentFormContent.displayName = 'XeroAmaxPayBatchPaymentFormContent';
